import type { PromptInputMessage } from '@crowdex/ui/ai/prompt-input';
import { useCallback, useEffect, useRef } from 'react';
import { useOnline } from '@/lib/online-status';
import type {
  DesktopShellStore,
  FollowUpQueuePause,
  QueuedFollowUp,
  SteeredFollowUp,
} from './store';

/** Owns steering, transcript finalization, and idle queue draining. */
export function useFollowUpDrain(deps: {
  store: DesktopShellStore;
  chatId: string;
  busy: boolean;
  agentReady: boolean;
  activeSteeredFollowUps: readonly SteeredFollowUp[];
  activeQueuedFollowUps: readonly QueuedFollowUp[];
  followUpQueuePaused: boolean;
  steer: (message: PromptInputMessage, messageId: string) => Promise<boolean>;
  /** Resolves `false` when nothing was sent (its cloud sandbox could not start): it waits again. */
  sendMessage: (message: PromptInputMessage) => Promise<unknown>;
  removeQueuedFollowUp: (conversationId: string, id: string) => void;
  enqueueFollowUp: (conversationId: string, message: PromptInputMessage, id?: string) => void;
}) {
  // The conversations whose queued message is on its way (a cloud start takes a while): each
  // drains on its own.
  const drainingRef = useRef(new Set<string>());
  const steeringRequestsRef = useRef(new Map<string, Promise<boolean>>());
  const recoveredSteeringIdsRef = useRef(new Set<string>());
  const steeringPending = deps.activeSteeredFollowUps.some((item) => item.status === 'pending');

  const steerFollowUp = useCallback(
    (message: PromptInputMessage, id = `steer-${crypto.randomUUID()}`): Promise<boolean> => {
      const existing = steeringRequestsRef.current.get(id);
      if (existing !== undefined) return existing;
      recoveredSteeringIdsRef.current.delete(id);
      const { setSteeredFollowUps } = deps.store.getState();
      const followUp: SteeredFollowUp = { id, message, status: 'pending' };
      setSteeredFollowUps((current) => ({
        ...current,
        [deps.chatId]: [...(current[deps.chatId] ?? []).filter((item) => item.id !== id), followUp],
      }));
      let request!: Promise<boolean>;
      request = (async () => {
        let accepted = false;
        try {
          for (let attempt = 0; attempt < 3; attempt += 1) {
            try {
              accepted = await deps.steer(message, id);
              break;
            } catch {
              // The request may have reached the runtime before its response
              // was lost. Retrying with the same id is safe end-to-end.
            }
          }
          if (recoveredSteeringIdsRef.current.delete(id)) return false;
          if (accepted) {
            setSteeredFollowUps((current) => ({
              ...current,
              [deps.chatId]: [
                ...(current[deps.chatId] ?? []).filter((item) => item.id !== id),
                { ...followUp, status: 'accepted' },
              ],
            }));
          }
          return accepted;
        } finally {
          if (steeringRequestsRef.current.get(id) === request) {
            steeringRequestsRef.current.delete(id);
          }
        }
      })().then((accepted) => {
        if (!accepted) {
          setSteeredFollowUps((current) => ({
            ...current,
            [deps.chatId]: (current[deps.chatId] ?? []).filter((item) => item.id !== id),
          }));
        }
        return accepted;
      });
      steeringRequestsRef.current.set(id, request);
      return request;
    },
    [deps.chatId, deps.steer, deps.store],
  );

  // An accepted steer is already in the turn's message (the agent server wrote it there, where
  // it was sent): once the turn ends there is nothing left to show for it.
  const finalizeAcceptedSteers = useCallback(() => {
    const accepted = deps.activeSteeredFollowUps.filter((item) => item.status === 'accepted');
    if (accepted.length === 0) return;
    const acceptedIds = new Set(accepted.map((item) => item.id));
    deps.store.getState().setSteeredFollowUps((current) => ({
      ...current,
      [deps.chatId]: (current[deps.chatId] ?? []).filter((item) => !acceptedIds.has(item.id)),
    }));
  }, [deps.activeSteeredFollowUps, deps.chatId, deps.store]);

  useEffect(() => {
    if (deps.busy || deps.activeSteeredFollowUps.length === 0) return;
    finalizeAcceptedSteers();
    const pending = deps.activeSteeredFollowUps.filter((item) => item.status === 'pending');
    if (pending.length === 0) return;
    const pendingIds = new Set(pending.map((item) => item.id));
    for (const item of pending) recoveredSteeringIdsRef.current.add(item.id);
    deps.store.getState().setSteeredFollowUps((current) => ({
      ...current,
      [deps.chatId]: (current[deps.chatId] ?? []).filter(
        (item) => item.status !== 'pending' || !pendingIds.has(item.id),
      ),
    }));
    const queuedIds = new Set(deps.activeQueuedFollowUps.map((item) => item.id));
    for (const item of pending) {
      if (!queuedIds.has(item.id)) deps.enqueueFollowUp(deps.chatId, item.message, item.id);
    }
  }, [
    deps.activeQueuedFollowUps,
    deps.activeSteeredFollowUps,
    deps.busy,
    deps.chatId,
    deps.enqueueFollowUp,
    deps.store,
    finalizeAcceptedSteers,
  ]);

  const sendIdle = useCallback(
    (queued: QueuedFollowUp) => {
      const conversationId = deps.chatId;
      if (drainingRef.current.has(conversationId)) return;
      drainingRef.current.add(conversationId);
      finalizeAcceptedSteers();
      deps.removeQueuedFollowUp(deps.chatId, queued.id);
      deps.store
        .getState()
        .setPausedFollowUpQueues((current) => ({ ...current, [deps.chatId]: undefined }));
      const requeue = (reason: FollowUpQueuePause) => {
        deps.enqueueFollowUp(deps.chatId, queued.message, queued.id);
        deps.store
          .getState()
          .setPausedFollowUpQueues((current) => ({ ...current, [deps.chatId]: reason }));
      };
      void deps
        .sendMessage(queued.message)
        .then(
          (sent) => {
            if (sent === false) requeue('cloud-start-failed');
          },
          () => requeue('send-failed'),
        )
        .finally(() => {
          drainingRef.current.delete(conversationId);
        });
    },
    [
      deps.chatId,
      deps.enqueueFollowUp,
      deps.removeQueuedFollowUp,
      deps.sendMessage,
      deps.store,
      finalizeAcceptedSteers,
    ],
  );

  // Offline, queued messages wait for the network (design D53).
  const online = useOnline();
  useEffect(() => {
    const next = deps.activeQueuedFollowUps[0];
    if (
      !online ||
      deps.busy ||
      steeringPending ||
      deps.followUpQueuePaused ||
      !deps.agentReady ||
      next === undefined
    )
      return;
    sendIdle(next);
  }, [
    deps.activeQueuedFollowUps,
    deps.agentReady,
    deps.busy,
    deps.followUpQueuePaused,
    online,
    sendIdle,
    steeringPending,
  ]);

  const sendQueuedFollowUp = useCallback(
    (queued: QueuedFollowUp) => {
      if (!deps.busy) {
        sendIdle(queued);
        return;
      }
      void steerFollowUp(queued.message, queued.id).then((accepted) => {
        if (accepted) deps.removeQueuedFollowUp(deps.chatId, queued.id);
      });
    },
    [deps.busy, deps.chatId, deps.removeQueuedFollowUp, sendIdle, steerFollowUp],
  );

  return { finalizeAcceptedSteers, sendQueuedFollowUp, steerFollowUp };
}

/** The agent's question (its tool call) a steered message answers, when it answers one. */
export function answeredQuestion(message: PromptInputMessage): string | undefined {
  const answers = (message as PromptInputMessage & { answers?: unknown }).answers;
  return typeof answers === 'string' ? answers : undefined;
}
